import React, { useEffect, useState, useCallback, useRef } from "react";
import { Search, FileDown, RefreshCw, Paperclip, Repeat, Flag } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { downloadWithAuth, openWithAuth } from "../utils/download";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
const BE  = process.env.REACT_APP_BACKEND_URL || "";

const STATUS_COLORS = {
  Pending:   "bg-amber-50 text-amber-700 border-amber-200",
  Confirmed: "bg-green-50 text-green-700 border-green-200",
  Completed: "bg-blue-50 text-blue-700 border-blue-200",
  Cancelled: "bg-red-50 text-red-700 border-red-200",
  Blocked:   "bg-gray-100 text-gray-600 border-gray-200",
};


// ID proofs are identity documents and now sit behind an authenticated
// route (GET /api/id-proofs/:filename), which a plain <a href> can't reach
// — fetch with auth and open the blob instead. Records written before that
// change still hold a /uploads/... URL; those keep working as direct links
// until migration 003 rewrites them, so this degrades rather than breaks.
function IdProofLink({ url, index, authHeaders, compact }) {
  const isPrivate = url.startsWith("/api/");
  const cls = compact
    ? "flex items-center gap-1 px-1.5 py-1 rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 font-medium transition-colors"
    : "flex items-center gap-1 text-xs px-2 py-1 rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 font-medium transition-colors";
  const label = compact ? index + 1 : `ID ${index + 1}`;

  if (!isPrivate) {
    return (
      <a href={url.startsWith("http") ? url : `${BE}${url}`} target="_blank" rel="noopener noreferrer"
        title={`ID proof ${index + 1}`} className={cls}>
        <Paperclip size={10} /> {label}
      </a>
    );
  }
  return (
    <button type="button" title={`ID proof ${index + 1}`} className={cls}
      onClick={() => openWithAuth(`${API}${url.replace(/^\/api/, "")}`, authHeaders).catch(e => alert(e.message))}>
      <Paperclip size={10} /> {label}
    </button>
  );
}

export default function CustomersPage() {
  const { authHeaders } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage]           = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [sortBy, setSortBy] = useState("newest");
  const [exportError, setExportError] = useState("");

  async function exportCsv() {
    setExportError("");
    try { await downloadWithAuth(`${API}/customers/csv`, authHeaders, "customers.csv"); }
    catch (e) { setExportError(e.message); }
  }

  // Debounce the search box so it doesn't hit the server on every keystroke,
  // and jump back to page 1 whenever the query changes (a stale page number
  // from a previous, larger result set would otherwise come back empty).
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  // Guards against out-of-order responses: an earlier request (e.g. the
  // unfiltered initial load) can resolve after a later, narrower search
  // request and clobber it. Only the most recently *issued* request is
  // allowed to write to state.
  const requestIdRef = useRef(0);
  const load = useCallback(async () => {
    const reqId = ++requestIdRef.current;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 50, sort: sortBy });
      if (debouncedSearch) params.append("search", debouncedSearch);
      const res  = await fetch(`${API}/customers?${params}`, { headers: authHeaders() });
      const json = await res.json();
      if (reqId === requestIdRef.current) {
        setCustomers(json.data || []);
        setTotalPages(json.pages || 1);
        setTotalCount(json.total ?? (json.data || []).length);
      }
    } catch {}
    if (reqId === requestIdRef.current) setLoading(false);
  }, [page, debouncedSearch, sortBy]);

  useEffect(() => { load(); }, [load]);

  const filtered = customers;

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Customers</h1>
          <p className="text-sm text-gray-500">{totalCount} guest record{totalCount === 1 ? "" : "s"}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="tap-44 p-2 hover:bg-gray-100 rounded-xl transition-colors">
            <RefreshCw size={16} className={`text-gray-500 ${loading ? "animate-spin" : ""}`} />
          </button>
          <button onClick={exportCsv}
            className="tap-44 flex items-center gap-2 text-sm font-medium text-gray-600
              bg-white border border-gray-200
              px-3 py-2 rounded-xl shadow-sm hover:shadow transition-all">
            <FileDown size={15} /> Export CSV
          </button>
        </div>
      </div>
      {exportError && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">{exportError}</p>}

      <div className="flex flex-wrap gap-3 bg-white p-4 rounded-2xl border border-gray-100 shadow-sm">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search name, ref, phone, travel from/to…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200
 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400" />
        </div>
        <select value={sortBy} onChange={e => { setSortBy(e.target.value); setPage(1); }}
          className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white">
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
          <option value="checkInAsc">Check-in (Earliest)</option>
          <option value="checkInDesc">Check-in (Latest)</option>
          <option value="refAsc">Booking Ref (A→Z)</option>
        </select>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="hidden md:block overflow-auto max-h-[65vh]">
          <table className="text-xs">
            <thead className="sticky top-0 z-10 bg-white">
              <tr className="border-b border-gray-100">
                {["Timestamp","Guests","Name(s)","Travel From","Travel To","Purpose","Booked Via","WhatsApp","Status","ID Proofs","Booking Ref"].map(h => (
                  <th key={h} className="text-left text-[10.5px] font-semibold text-gray-400
                    uppercase tracking-wider px-3 py-2 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <tr><td colSpan={11} className="text-center py-12 text-gray-400">
                  <RefreshCw size={20} className="animate-spin mx-auto mb-2" /> Loading…
                </td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={11} className="text-center py-12 text-gray-400">No customer records yet</td></tr>
              ) : filtered.map(c => (
                <tr key={c.bookingId} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-3 py-2 text-gray-500 whitespace-nowrap">
                    {c.timestamp ? new Date(c.timestamp).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                  </td>
                  <td className="px-3 py-2 text-center text-gray-700">{c.numberOfGuests}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      <p className="font-medium text-gray-900 truncate max-w-[160px]">
                        {c.fullNames.join(", ") || "—"}
                      </p>
                      {c.isRepeatGuest && (
                        <span title="Booked with us before"
                          className="flex items-center gap-1 text-[9.5px] font-semibold px-1.5 py-0.5
                            rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200
                            whitespace-nowrap flex-shrink-0">
                          <Repeat size={9} /> Repeat
                        </span>
                      )}
                      {c.guestFlag && (
                        <span title={c.guestFlag.reason}
                          className="flex items-center gap-1 text-[9.5px] font-semibold px-1.5 py-0.5
                            rounded-full bg-red-50 text-red-700 border border-red-200
                            whitespace-nowrap flex-shrink-0">
                          <Flag size={9} /> Flagged
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-gray-700">{c.travelFrom || "—"}</td>
                  <td className="px-3 py-2 text-gray-700">{c.travelTo || "—"}</td>
                  <td className="px-3 py-2 text-gray-700 max-w-[140px] truncate">{c.purposeOfVisit || "—"}</td>
                  <td className="px-3 py-2 text-gray-700">{c.bookingMadeThrough}</td>
                  <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{c.whatsapp || "—"}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[10.5px] font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap
                      ${STATUS_COLORS[c.status] || "bg-gray-100 text-gray-600 border-gray-200"}`}>
                      {c.status || "—"}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {c.identityProofUrls.length === 0 ? "—" : (
                      <div className="flex items-center gap-1 flex-nowrap">
                        {c.identityProofUrls.map((u, i) => (
                          <IdProofLink key={i} url={u} index={i} authHeaders={authHeaders} compact />
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono text-gray-500 whitespace-nowrap">{c.bookingRef}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="md:hidden divide-y divide-gray-100 max-h-[75vh] overflow-auto">
          {loading ? (
            <div className="text-center py-12 text-gray-400">
              <RefreshCw size={20} className="animate-spin mx-auto mb-2" /> Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-400">No customer records yet</div>
          ) : (
            filtered.map((c) => (
              <div key={c.bookingId} className="p-3.5 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <p className="font-semibold text-gray-900 text-sm truncate">{c.fullNames.join(", ") || "—"}</p>
                      {c.isRepeatGuest && (
                        <span title="Booked with us before"
                          className="flex items-center gap-1 text-[9.5px] font-semibold px-1.5 py-0.5
                            rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 whitespace-nowrap flex-shrink-0">
                          <Repeat size={9} /> Repeat
                        </span>
                      )}
                      {c.guestFlag && (
                        <span title={c.guestFlag.reason}
                          className="flex items-center gap-1 text-[9.5px] font-semibold px-1.5 py-0.5
                            rounded-full bg-red-50 text-red-700 border border-red-200 whitespace-nowrap flex-shrink-0">
                          <Flag size={9} /> Flagged
                        </span>
                      )}
                    </div>
                    <p className="text-[10.5px] font-mono text-gray-400">{c.bookingRef}</p>
                  </div>
                  <span className={`text-[10.5px] font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap flex-shrink-0
                    ${STATUS_COLORS[c.status] || "bg-gray-100 text-gray-600 border-gray-200"}`}>
                    {c.status || "—"}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-gray-600">
                  <p><span className="text-gray-400">Guests </span>{c.numberOfGuests}</p>
                  <p className="truncate"><span className="text-gray-400">Via </span>{c.bookingMadeThrough}</p>
                  <p className="truncate"><span className="text-gray-400">From </span>{c.travelFrom || "—"}</p>
                  <p className="truncate"><span className="text-gray-400">To </span>{c.travelTo || "—"}</p>
                </div>

                {c.purposeOfVisit && <p className="text-xs text-gray-500">Purpose: {c.purposeOfVisit}</p>}

                <div className="flex items-center justify-between text-xs border-t border-gray-100 pt-2">
                  <span className="text-gray-500">{c.whatsapp || "—"}</span>
                  <span className="text-gray-400">
                    {c.timestamp ? new Date(c.timestamp).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                  </span>
                </div>

                {c.identityProofUrls.length > 0 && (
                  <div className="flex items-center gap-1 flex-wrap pt-1">
                    {c.identityProofUrls.map((u, i) => (
                      <IdProofLink key={i} url={u} index={i} authHeaders={authHeaders} />
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="text-sm px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">
              Previous
            </button>
            <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="text-sm px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
